import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Building2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

// Crea un'azienda dal backoffice: organizzazione + account referente insieme
// (POST /admin/organizations). Stessi dati del form pubblico di registrazione
// azienda (dati azienda + indirizzo di fatturazione + credenziali). A differenza
// del self-signup, l'account nasce già operativo e la password può essere
// generata in automatico.
const EMPTY = {
  ragione_sociale: "", vat_number: "", tax_code: "", sdi_code: "", pec: "", phone: "",
  billing_address: { street: "", cap: "", city: "", province: "", country: "IT" },
  email: "", password: "",
};

export default function NewOrgDialog({ open, onOpenChange, onCreated, onGeneratedPassword }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(EMPTY); }, [open]);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setAddr = (k) => (e) => setForm({ ...form, billing_address: { ...(form.billing_address || {}), [k]: e.target.value } });
  const addr = form.billing_address || {};

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        company: {
          ragione_sociale: form.ragione_sociale,
          vat_number: form.vat_number.trim() || null,
          tax_code: form.tax_code.trim() || null,
          sdi_code: form.sdi_code.trim() || null,
          pec: form.pec.trim() || null,
          phone: form.phone.trim() || null,
          billing_address: form.billing_address,
        },
        email: form.email,
      };
      if (form.password) body.password = form.password;
      const { data } = await api.post("/admin/organizations", body);
      onCreated?.(data);
      // Percorso primario: il referente imposta la password dal link d'invito
      // ricevuto via email. La password generata resta solo come ripiego.
      toast.success(`Azienda creata. Invito inviato a ${data.account.email}.`);
      if (data.generated_password) {
        onGeneratedPassword?.({
          email: data.account.email,
          password: data.generated_password,
          title: "Invito inviato",
          subtitle: `${data.account.email} riceverà un'email per impostare la propria password e accedere. Comunica la password qui sotto solo come ripiego, se l'email non arriva.`,
        });
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-brand-blue flex items-center gap-2"><Building2 size={18} /> Nuova azienda</DialogTitle>
          <DialogDescription>
            Stessi dati della registrazione azienda. Il referente riceverà un’email con un link per impostare la propria password: il campo password è opzionale (di norma vuoto).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-6">
          <section>
            <h3 className="text-sm font-semibold text-brand-ink mb-3 uppercase tracking-wide">Dati azienda</h3>
            <div className="space-y-4">
              <Fld label="Ragione sociale" value={form.ragione_sociale} onChange={setF("ragione_sociale")} required />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Fld label="Partita IVA" value={form.vat_number} onChange={setF("vat_number")} />
                <Fld label="Codice fiscale (se ditta individuale)" value={form.tax_code} onChange={setF("tax_code")} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Fld label="Codice SDI" value={form.sdi_code} onChange={setF("sdi_code")} />
                <Fld label="PEC" value={form.pec} onChange={setF("pec")} />
              </div>
              <Fld label="Indirizzo (via e civico)" value={addr.street || ""} onChange={setAddr("street")} />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Fld label="CAP" value={addr.cap || ""} onChange={setAddr("cap")} />
                <Fld label="Città" value={addr.city || ""} onChange={setAddr("city")} />
                <Fld label="Prov." value={addr.province || ""} onChange={setAddr("province")} />
                <Fld label="Telefono" value={form.phone} onChange={setF("phone")} />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-brand-ink mb-3 uppercase tracking-wide">Credenziali di accesso</h3>
            <p className="text-xs text-brand-muted -mt-2 mb-3">Il referente accede con questa email e riceve un invito per impostare da sé la password.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Fld label="Email di accesso" type="email" value={form.email} onChange={setF("email")} required />
              <Fld label="Password (opzionale)" value={form.password} onChange={setF("password")} placeholder="Vuota: la imposta il referente via invito" />
            </div>
          </section>

          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="px-4 py-2 text-sm text-brand-muted hover:text-brand-ink">Annulla</button>
            <Button type="submit" className="btn-primary" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Crea azienda"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, ...props }) {
  return (
    <div>
      <Label className="text-sm text-brand-ink">{label}</Label>
      <Input {...props} className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue" />
    </div>
  );
}
